<template>
  <TransitionRoot appear :show="showModal" as="template">
    <Dialog as="div" @close="closeModal">
      <div class="fixed inset-0 z-30 overflow-y-auto">
        <div class="min-h-screen text-center flex">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0"
            enter-to="opacity-10"
            leave="duration-200 ease-in"
            leave-from="opacity-10"
            leave-to="opacity-0"
          >
            <DialogOverlay class="fixed inset-0 bg-black opacity-30" />
          </TransitionChild>

          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-50"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-50"
          >
            <div
              class="inline-block w-full my-5 mx-3 lg:w-2/3 md:mx-auto max-w-screen-md m-auto overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded"
            >
              <div class="flex flex-row">
                <div class="flex-1"></div>
                <div class="flex-4 pr-2 pt-2 cursor-pointer" @click="closeModal(false)">
                  <XIcon class="text-brand-dark-gray w-7 h-7" ref="close" />
                </div>
              </div>

              <div class="pb-5 px-5 md:px-8">
                <template v-if="!showList">
                  <h2 class="text-xl text-brand-black py-2">Share application</h2>

                  <p
                    class="py-3 text-brand-green cursor-pointer hover:underline"
                    @click="toggleShow"
                  >
                    View who the application is shared with
                  </p>

                  <p class="py-2">
                    When you share an application, we'll send an email with a secure link to view
                    the submitted application. You can decide if you'd like screening reports
                    included, and whether you'd like the sharee to have the ability to approve or
                    reject the applicant.
                  </p>

                  <div class="flex flex-row py-2">
                    <div class="flex items-center mr-3">
                      <QuestionMarkCircleIcon class="text-brand-mid-gray w-5 h-5" />
                    </div>
                    <div class="flex items-center">
                      <p class="text-sm">
                        Please note that if the application has an Experian credit report, your
                        sharees won't be able to view the report due to Experian policies.
                      </p>
                    </div>
                  </div>

                  <div class="flex flex-col">
                    <div class="flex flex-col" v-for="(recipient, idx) in recipients" :key="idx">
                      <div class="flex w-full items-center justify-end" v-if="idx !== 0">
                        <XIcon
                          class="w-5 h-5 text-brand-red cursor-pointer"
                          @click="removeSharee(idx)"
                        />
                      </div>
                      <div class="flex flex-col md:flex-row md:justify-between">
                        <div class="flex flex-col py-2 md:w-1/2 md:mr-5">
                          <label for="name" class="block">
                            Email
                            <span class="text-brand-red">*</span>
                          </label>
                          <SingleLineInput
                            id="email"
                            type="email"
                            :value="recipient.email"
                            @input="recipient.email = $event.target.value"
                            :required="true"
                            :placeholder="'Enter email address'"
                          ></SingleLineInput>
                        </div>
                        <div class="flex flex-col py-2 md:w-1/2 md:mr-5">
                          <label for="name" class="block"> Name </label>
                          <SingleLineInput
                            id="name"
                            type="text"
                            :value="recipient.name"
                            @input="recipient.name = $event.target.value"
                          ></SingleLineInput>
                        </div>
                        <div class="flex flex-col py-2 md:w-1/2">
                          <label for="userType" class="block py-1">
                            User Type
                            <span class="text-brand-red">*</span>
                          </label>
                          <DropDown
                            :options="userTypeOptions"
                            @select="selectUserType(recipient, $event)"
                            :placeholder="'Select user type'"
                          ></DropDown>
                        </div>
                      </div>

                      <div class="flex flex-col md:flex-row">
                        <div class="flex flex-col py-2 md:w-1/2 md:mr-5">
                          <label for="userType" class="block">
                            Decisioning
                            <span class="text-brand-red">*</span>
                          </label>
                          <p class="text-xs italic text-brand-mid-gray">
                            Allow user to accept or reject the applicant
                          </p>
                          <DropDown
                            :options="booleanOptions"
                            @select="selectDecision(recipient, $event)"
                            :placeholder="'Select'"
                          ></DropDown>
                        </div>
                        <div class="flex flex-col py-2 md:w-1/2">
                          <label for="userType" class="block">
                            Reports
                            <span class="text-brand-red">*</span>
                          </label>
                          <p class="text-xs italic text-brand-mid-gray">
                            Allow user to view processed reports
                          </p>
                          <DropDown
                            :options="booleanOptions"
                            @select="selectReports(recipient, $event)"
                            :placeholder="'Select'"
                          ></DropDown>
                        </div>
                      </div>
                      <Line class="my-3" v-if="idx < recipients.length - 1" />
                    </div>
                    <div class="flex items-center my-2" style="width: 140px">
                      <SecondaryButton @click="addSharee">
                        <div class="flex flex-row">
                          <div class="flex items-center mr-1">
                            <PlusIcon class="text-brand-green w-4 h-4" />
                          </div>
                          <div class="flex items-center">
                            <p class="text-sm font-normal">Add sharee</p>
                          </div>
                        </div>
                      </SecondaryButton>
                    </div>

                    <div class="flex flex-col py-3">
                      <p>Extra Message</p>
                      <MultiLineInput
                        :placeholder="'Add a message for everyone on this list. (optional)'"
                        :value="extraMessage"
                        @input="extraMessage = $event.target.value"
                      />
                    </div>
                  </div>

                  <div class="flex flex-row w-full justify-end py-1" v-if="errorMessage">
                    <p class="text-brand-red">{{ errorMessage }}</p>
                  </div>

                  <div class="flex flex-row w-full py-3">
                    <div
                      class="flex w-full flex-col-reverse mx-auto md:mx-0 md:flex-row md:justify-end"
                    >
                      <p
                        class="text-center py-3 cursor-pointer md:pr-10"
                        @click="closeModal(false)"
                      >
                        Cancel
                      </p>
                      <div class="w-full md:w-1/3">
                        <PrimaryButton
                          @click="sendShare"
                          :disabled="isApplicationBundleLoading || isApplicationLoading"
                          :is-loading="isApplicationBundleLoading || isApplicationLoading"
                        >
                          Send application link
                        </PrimaryButton>
                      </div>
                    </div>
                  </div>
                </template>

                <template v-else>
                  <h2 class="text-xl text-brand-black py-2">Shared with</h2>

                  <p
                    class="py-3 text-brand-green cursor-pointer hover:underline"
                    @click="toggleShow"
                  >
                    Add new share
                  </p>

                  <div class="flex flex-row pt-3 pb-1">
                    <!-- START DESKTOP HEADER -->
                    <div class="hidden md:flex w-1/3">
                      <h5>Name & Email</h5>
                    </div>

                    <div class="hidden md:flex w-1/6">
                      <h5>Date</h5>
                    </div>

                    <div class="hidden md:flex w-1/6">
                      <h5>Status</h5>
                    </div>

                    <div class="hidden md:flex w-1/3">
                      <h5>Permissions</h5>
                    </div>
                    <!-- END DESKTOP HEADER -->

                    <div class="flex md:hidden items-center w-1/2">
                      <h5>Name, email, & permissions</h5>
                    </div>
                    <div class="flex md:hidden w-1/2 justify-end items-center">
                      <h5>Date</h5>
                    </div>
                  </div>
                  <div class="flex md:hidden border border-brand-light-gray w-full my-3"></div>

                  <div class="flex flex-col md:py-1 md:px-1 mb-10 md:bg-brand-light-gray">
                    <div
                      class="flex flex-row bg-white py-3 md:px-2 md:my-2"
                      v-for="(share, index) in app.shares"
                      :key="index"
                      :class="[index !== 0 ? 'border-t border-brand-light-gray md:border-0' : '']"
                    >
                      <!-- START DESKTOP ROW -->
                      <div class="hidden md:flex md:flex-col w-1/3">
                        <p>
                          {{
                            share.shared_to.name ||
                            share.shared_to.display_email ||
                            "Name Not Entered"
                          }}
                        </p>
                        <p class="text-xs text-brand-mid-gray italic">
                          {{ share.shared_to.display_email }}
                        </p>
                      </div>

                      <div class="hidden md:flex items-center w-1/6">
                        <p class="italic">{{ formatDate(share.created) }}</p>
                      </div>

                      <div class="hidden md:flex items-center w-1/6">
                        <p
                          class="capitalize"
                          :class="[
                            statusMap[share.status]
                              ? `text-brand-${statusMap[share.status].color}`
                              : ''
                          ]"
                        >
                          {{ statusMap[share.status] ? statusMap[share.status].label : "sent" }}
                        </p>
                      </div>

                      <div class="hidden md:flex items-center w-1/3">
                        <div
                          class="flex items-center rounded-lg py-1 px-1 mr-1"
                          :class="[
                            share.reports ? 'bg-brand-background-light-blue' : 'bg-brand-light-gray'
                          ]"
                        >
                          <XIcon v-if="!share.reports" class="text-brand-mid-gray w-4 h-4" />
                          <CheckIcon v-if="share.reports" class="text-brand-blue w-4 h-4" />
                          <p
                            :class="[
                              'text-xs',
                              share.reports ? 'text-brand-blue' : 'text-brand-mid-gray'
                            ]"
                          >
                            Reports
                          </p>
                        </div>

                        <div
                          class="flex items-center rounded-lg py-1 px-1"
                          :class="[
                            share.decisioning
                              ? 'bg-brand-background-light-blue'
                              : 'bg-brand-light-gray'
                          ]"
                        >
                          <XIcon v-if="!share.decisioning" class="text-brand-mid-gray w-4 h-4" />
                          <CheckIcon v-if="share.decisioning" class="text-brand-blue w-4 h-4" />
                          <p
                            :class="[
                              'text-xs',
                              share.decisioning ? 'text-brand-blue' : 'text-brand-mid-gray'
                            ]"
                          >
                            Decisioning
                          </p>
                        </div>
                      </div>
                      <!-- END DESKTOP ROW -->

                      <!-- START MOBILE ROW -->
                      <div class="flex flex-col md:hidden w-1/2">
                        <div class="flex items-center">
                          <p>
                            {{ share.shared_to.name || share.shared_to.display_email }}
                          </p>
                          <p
                            class="capitalize py-1 ml-3"
                            :class="[
                              statusMap[share.status]
                                ? `text-brand-${statusMap[share.status].color}`
                                : ''
                            ]"
                          >
                            {{ statusMap[share.status] ? statusMap[share.status].label : "sent" }}
                          </p>
                        </div>
                        <p class="text-xs text-brand-mid-gray italic py-1">
                          {{ share.shared_to.display_email }}
                        </p>
                        <div class="flex flex-row py-1">
                          <div
                            class="flex items-center rounded-lg py-1 px-1 mr-1"
                            :class="[
                              share.reports
                                ? 'bg-brand-background-light-blue'
                                : 'bg-brand-light-gray'
                            ]"
                          >
                            <XIcon v-if="!share.reports" class="text-brand-mid-gray w-4 h-4" />
                            <CheckIcon v-if="share.reports" class="text-brand-blue w-4 h-4" />
                            <p
                              :class="[
                                'text-xs',
                                share.reports ? 'text-brand-blue' : 'text-brand-mid-gray'
                              ]"
                            >
                              Reports
                            </p>
                          </div>

                          <div
                            class="flex items-center rounded-lg py-1 px-1"
                            :class="[
                              share.decisioning
                                ? 'bg-brand-background-light-blue'
                                : 'bg-brand-light-gray'
                            ]"
                          >
                            <XIcon v-if="!share.decisioning" class="text-brand-mid-gray w-4 h-4" />
                            <CheckIcon v-if="share.decisioning" class="text-brand-blue w-4 h-4" />
                            <p
                              :class="[
                                'text-xs',
                                share.decisioning ? 'text-brand-blue' : 'text-brand-mid-gray'
                              ]"
                            >
                              Decisioning
                            </p>
                          </div>
                        </div>
                      </div>

                      <div class="flex md:hidden w-1/2 justify-end">
                        <p class="italic py-1">{{ formatDate(share.created) }}</p>
                      </div>
                      <!-- END MOBILE ROW -->
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
<script>
import { mapGetters } from "vuex"
import moment from "moment"

import { TransitionRoot, TransitionChild, Dialog, DialogOverlay } from "@headlessui/vue"

import { XIcon, QuestionMarkCircleIcon, CheckIcon, PlusIcon } from "@heroicons/vue/outline"

import { APPLICATION_BUNDLE_SHARE } from "@/store/types"

export default {
  props: {
    showModal: {
      type: Boolean,
      default: false
    },
    app: {
      type: Object
    }
  },
  components: {
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogOverlay,
    XIcon,
    QuestionMarkCircleIcon,
    CheckIcon,
    PlusIcon
  },
  computed: {
    ...mapGetters(["isApplicationBundleLoading", "isApplicationLoading"])
  },
  data() {
    return {
      showList: false,
      statusMap: {
        sent: {
          label: "delivered",
          color: "yellow"
        },
        open: {
          label: "opened",
          color: "green"
        },
        clicked: {
          label: "clicked",
          color: "green"
        },
        rejected: {
          label: "bounced",
          color: "red"
        }
      },
      userTypeOptions: [
        { value: "brokerage", label: "Agent" },
        { value: "property_manager", label: "Property Manager" },
        { value: "landlord", label: "Independent Landlord" }
      ],
      booleanOptions: [
        { value: true, label: "Yes" },
        { value: false, label: "No" }
      ],
      extraMessage: "",
      recipients: [
        {
          reports: "",
          decisioning: "",
          userType: "",
          email: "",
          name: ""
        }
      ],
      errorMessage: ""
    }
  },
  mounted() {
    this.showList = !!this.app.shares.length
  },
  methods: {
    closeModal(reload = false) {
      this.$emit("close", reload)
    },
    toggleShow() {
      this.showList = !this.showList
    },
    selectUserType(recipient, val) {
      recipient.userType = val.value
    },
    selectReports(recipient, val) {
      recipient.reports = val.value
    },
    selectDecision(recipient, val) {
      recipient.decisioning = val.value
    },
    formatDate(date) {
      return moment(date).format("MM/DD/YY")
    },
    addSharee() {
      this.recipients.push({
        reports: "",
        decisioning: "",
        userType: "",
        email: "",
        name: ""
      })
    },
    removeSharee(idx) {
      this.recipients.splice(idx, 1)
    },
    async sendShare() {
      if (
        !this.recipients.every(
          (r) =>
            r.userType &&
            r.email &&
            typeof r.reports === "boolean" &&
            typeof r.decisioning === "boolean"
        )
      ) {
        return
      }

      const { recipients, extraMessage } = this
      const { bundle } = this.app

      try {
        await this.$store.dispatch(APPLICATION_BUNDLE_SHARE, {
          slug: bundle.slug,
          recipients,
          extraMessage,
          baseUrl: this.baseHostName
        })
        this.closeModal(true)
      } catch (err) {
        if (err.response) {
          this.errorMessage = err.response.data.message
        } else {
          this.errorMessage = "Error has occurred"
        }

        setTimeout(() => {
          this.errorMessage = ""
        }, 3000)
      }
    }
  }
}
</script>
